import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
// SEO: tutti i meta tag e JSON-LD sono iniettati esclusivamente in SSR (entry-server-conditional.tsx)
import { MapPin, Store, ShieldCheck, ArrowRight, CheckCircle, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useStore } from '@/store/useStore';
import { supabase } from '@/integrations/supabase/client';
import { Dealer } from '@/types/dealer';
import { FALLBACK_METAL_PRICES } from '@/server/config/ssr-config';
import { getZonaUrbana, ZONE_URBANE_RENDERABLE, ZonaUrbana } from '@/lib/zoneUrbaneData';

/**
 * SEO Phase 4 — Landing dinamica per Zona Urbana
 * Pattern URL: /compro-oro/:città/:zona (whitelist via zoneUrbaneData)
 */

interface Props {
  ssrDealers?: any[];
  ssrMetalPrices?: any;
  ssrQuotationsMap?: Record<string, any>;
  ssrZona?: ZonaUrbana | null;
}

let cachedSSRContext: any = null;
let ssrContextRead = false;
function getSSRContext(): any {
  if (ssrContextRead) return cachedSSRContext;
  ssrContextRead = true;
  if (typeof window === 'undefined') return null;
  try {
    const el = document.getElementById('__SSR_CONTEXT__');
    cachedSSRContext = el?.textContent ? JSON.parse(el.textContent) : null;
  } catch { cachedSSRContext = null; }
  return cachedSSRContext;
}

export default function ComproOroZonaUrbana({ ssrDealers, ssrZona }: Props = {}) {
  const params = useParams<{ city?: string; zona?: string }>();
  const citySlug = (params.city || '').toLowerCase();
  const zoneSlug = (params.zona || '').toLowerCase();

  const zona = ssrZona || getZonaUrbana(citySlug, zoneSlug);
  const { currentGoldPrice, currentSilverPrice } = useStore();

  // Zona non in whitelist: rimanda all'hub città
  if (!zona) {
    return <Navigate to={`/compro-oro/${citySlug}`} replace />;
  }

  const [dealers, setDealers] = useState<Dealer[]>(() => {
    const source = ssrDealers || (typeof window !== 'undefined' ? getSSRContext()?.zonaUrbanaDealers : null);
    if (!source || source.length === 0) return [];
    return source.map((d: any) => ({
      id: d.id,
      name: d.name,
      latitude: d.latitude,
      longitude: d.longitude,
      city: d.city,
      address: d.address,
      oam_verified: d.oam_verified,
      opo_code: d.opo_code,
      goldPrice: 0,
      silverPrice: 0,
      gts_score: d.gts_score,
    } as any));
  });

  useEffect(() => {
    if (dealers.length > 0) return;
    let cancelled = false;
    (async () => {
      const { data: zoneRow } = await supabase
        .from('zone_subcomunali' as any)
        .select('id')
        .eq('nome_slug', zona.zoneSlug)
        .limit(1)
        .maybeSingle();
      if (cancelled || !zoneRow?.id) return;
      const { data: list } = await supabase
        .from('dealers')
        .select('id, name, latitude, longitude, city, address, oam_verified, opo_code')
        .eq('zona_subcomunale_id', zoneRow.id)
        .limit(20);
      if (cancelled || !Array.isArray(list) || list.length === 0) {
        if (!cancelled && Array.isArray(list)) setDealers(list as any);
        return;
      }
      const ids = list.map((d: any) => d.id);
      const { data: scores } = await supabase
        .from('merchant_gts_snapshot' as any)
        .select('dealer_id, gts_score')
        .in('dealer_id', ids);
      if (cancelled) return;
      const scoreMap = new Map<string, number>();
      (scores || []).forEach((s: any) => scoreMap.set(s.dealer_id, Number(s.gts_score) || 0));
      const merged = list
        .map((d: any) => ({ ...d, gts_score: scoreMap.get(d.id) ?? 0 }))
        .sort((a: any, b: any) => (b.gts_score - a.gts_score) || a.name.localeCompare(b.name));
      setDealers(merged as any);
    })();
    return () => { cancelled = true; };
  }, [zona.zoneSlug, dealers.length]);

  const goldPrice = (typeof currentGoldPrice === 'number' && currentGoldPrice > 0) ? currentGoldPrice : FALLBACK_METAL_PRICES.gold.price;
  const silverPrice = (typeof currentSilverPrice === 'number' && currentSilverPrice > 0) ? currentSilverPrice : FALLBACK_METAL_PRICES.silver.price;

  const faqs = useMemo(() => [
    {
      q: `Quanti compro oro ci sono a ${zona.zoneName}, ${zona.cityName}?`,
      a: `In zona ${zona.zoneName} (${zona.tipo}, popolazione ~${zona.popolazione.toLocaleString('it-IT')}) sono attivi diversi operatori OAM/OPO verificati. Su Gildy puoi confrontare prezzi e recensioni aggiornati in tempo reale.`,
    },
    {
      q: `Come si calcola il valore dell'oro usato a ${zona.zoneName}?`,
      a: `Il valore dipende da caratura (9-24KT), peso effettivo (escluse pietre) e prezzo spot dell'oro (oggi ≈${goldPrice.toFixed(2)} €/g a 24KT). I compro oro applicano uno spread di acquisto che su Gildy puoi confrontare a colpo d'occhio.`,
    },
    {
      q: `Quali documenti servono per vendere oro a ${zona.zoneName}?`,
      a: `In Italia tutti i compro oro autorizzati richiedono un documento d'identità in corso di validità e codice fiscale (D.Lgs. 92/2017). Il pagamento, se ≥500€, deve essere tracciato (bonifico/assegno).`,
    },
    {
      q: `Qual è il miglior compro oro in zona ${zona.zoneName}?`,
      a: `Su Gildy puoi ordinare i compro oro per quotazione live, recensioni verificate e Gildy Trust Score (GTS). Il "miglior" operatore dipende dal tipo di oggetto, dal peso e dal momento di vendita.`,
    },
  ], [zona, goldPrice]);

  // Zone limitrofe (max 6 nella stessa città)
  const sisterZones = ZONE_URBANE_RENDERABLE
    .filter(z => z.citySlug === zona.citySlug && z.zoneSlug !== zona.zoneSlug)
    .slice(0, 6);

  // URL canonico città-padre (single-dash) per evitare redirect 301
  const cityCanonical = `/compro-oro-${zona.citySlug}`;

  return (
    <>
      {/* Meta tags + JSON-LD (BreadcrumbList, FAQPage, Place, ItemList) sono iniettati lato SSR.
          Vedi entry-server-conditional.tsx (case 'zona-urbana'). */}

      <main className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-4">
          <Link to="/" className="hover:underline">Home</Link>
          {' / '}
          <Link to="/compro-oro" className="hover:underline">Compro Oro</Link>
          {' / '}
          <Link to={cityCanonical} className="hover:underline">{zona.cityName}</Link>
          {' / '}
          <span className="text-foreground font-medium">{zona.zoneName}</span>
        </nav>

        {/* Hero */}
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Compro Oro {zona.zoneName} — {zona.cityName}
          </h1>
          <p className="text-lg text-muted-foreground mb-4">
            Confronta le quotazioni dei migliori compro oro in zona <strong>{zona.zoneName}</strong> a {zona.cityName}: prezzi live, operatori OAM verificati, recensioni reali.
          </p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="gap-1"><MapPin className="w-3 h-3" />{zona.tipo}</Badge>
            <Badge variant="outline" className="gap-1"><Building className="w-3 h-3" />{zona.popolazione.toLocaleString('it-IT')} abitanti</Badge>
            <Badge variant="outline" className="gap-1"><ShieldCheck className="w-3 h-3" />OAM verificati</Badge>
          </div>
        </header>

        {/* Quote live */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-3">Quotazione oro live a {zona.zoneName}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-xs text-muted-foreground">Oro 24KT</div>
                <div className="font-bold text-lg">€ {goldPrice.toFixed(2)}/g</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Oro 18KT</div>
                <div className="font-bold text-lg">€ {(goldPrice * 0.75).toFixed(2)}/g</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Oro 14KT</div>
                <div className="font-bold text-lg">€ {(goldPrice * 0.585).toFixed(2)}/g</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Argento PURO</div>
                <div className="font-bold text-lg">€ {silverPrice.toFixed(2)}/g</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Intro zona */}
        <section className="prose prose-sm md:prose-base max-w-none mb-8">
          <h2>La zona {zona.zoneName} a colpo d'occhio</h2>
          <p>{zona.intro}</p>
          <p>
            Con una popolazione di circa <strong>{zona.popolazione.toLocaleString('it-IT')} abitanti</strong>, {zona.zoneName} è un'area
            con presenza consolidata di operatori del settore "compro oro". Su Gildy puoi confrontare in tempo reale
            le quotazioni di tutti i banchi metalli iscritti all'OAM e operare con la massima trasparenza.
          </p>
        </section>

        {/* Dealer list */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Store className="w-6 h-6" /> Compro Oro a {zona.zoneName}
          </h2>
          {dealers.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                <p>Stiamo aggiornando i dati dei compro oro in questa zona.</p>
                <Button asChild className="mt-3">
                  <Link to={cityCanonical}>Vedi tutti i compro oro di {zona.cityName}</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {dealers.slice(0, 12).map((d: any) => (
                <Link key={d.id} to={`/compro-oro/${zona.citySlug}/${d.id}`} className="block">
                  <Card className="hover:border-primary transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-semibold leading-tight">{d.name}</h3>
                        {d.oam_verified && <Badge variant="outline" className="gap-1 text-xs"><ShieldCheck className="w-3 h-3" />OAM</Badge>}
                      </div>
                      {d.address && <p className="text-xs text-muted-foreground flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />{d.address}</p>}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Come vendere */}
        <section className="prose prose-sm md:prose-base max-w-none mb-10">
          <h2>Come vendere oro a {zona.zoneName} in sicurezza</h2>
          <ol>
            <li><strong>Pesa l'oggetto</strong> separando le pietre e isolando le carature diverse (9-14-18-22-24KT).</li>
            <li><strong>Confronta le quotazioni</strong> live di almeno 3 compro oro su Gildy.</li>
            <li><strong>Verifica l'iscrizione OAM</strong> (Registro Operatori Compro Oro) — su Gildy è già evidenziata.</li>
            <li><strong>Richiedi sempre la ricevuta</strong> (obbligatoria per legge) e per importi ≥500€ esigi pagamento tracciato.</li>
            <li><strong>Blocca il prezzo</strong> sulla nostra piattaforma se i mercati sono volatili.</li>
          </ol>
          <p>
            Vuoi una stima rapida prima di muoverti? Usa il nostro <Link to="/calcolatore-oro-usato">calcolatore oro usato</Link> e
            confronta poi le offerte dei compro oro di zona.
          </p>
        </section>

        {/* FAQ */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold mb-4">Domande frequenti</h2>
          <div className="space-y-4">
            {faqs.map(f => (
              <Card key={f.q}>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-2 flex gap-2"><CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />{f.q}</h3>
                  <p className="text-sm text-muted-foreground">{f.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Zone limitrofe + link città canonico */}
        <section className="mb-8" aria-labelledby="zone-limitrofe">
          <h2 id="zone-limitrofe" className="text-xl font-bold mb-3">Esplora altre zone di {zona.cityName}</h2>
          <div className="flex flex-wrap gap-2">
            {sisterZones.map(s => (
              <Button key={s.zoneSlug} variant="outline" size="sm" asChild>
                <Link
                  to={`/compro-oro/${s.citySlug}/${s.zoneSlug}`}
                  title={`Compro Oro ${s.zoneName} ${zona.cityName}`}
                >
                  {s.zoneName}
                </Link>
              </Button>
            ))}
            <Button variant="secondary" size="sm" asChild>
              <Link to={cityCanonical} title={`Tutti i compro oro di ${zona.cityName}`}>
                Tutti i compro oro di {zona.cityName} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </section>

        {/* Link contestuali: solo approfondimenti NON già presenti nella nav globale,
            per evitare doppioni di calcolatore / prezzo oro / prezzo argento. */}
        <section className="mb-10" aria-labelledby="conversioni">
          <h2 id="conversioni" className="text-xl font-bold mb-3">
            Approfondimenti per vendere oro a {zona.zoneName}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Link
              to="/guida/vendere-oro-usato-in-sicurezza"
              title="Guida completa per vendere oro usato in sicurezza"
              className="block"
            >
              <Card className="h-full hover:border-primary transition-colors">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-1">Guida: vendere oro usato</h3>
                  <p className="text-xs text-muted-foreground">
                    10 mosse per ottenere il miglior prezzo ed evitare truffe in zona {zona.zoneName}.
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link
              to="/blocca-prezzo-oro"
              title="Blocca il prezzo dell'oro per 24-96 ore"
              className="block"
            >
              <Card className="h-full hover:border-primary transition-colors">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-1">Blocca il prezzo</h3>
                  <p className="text-xs text-muted-foreground">
                    Fissa la quotazione fino a 96h prima di recarti da un compro oro a {zona.zoneName}.
                  </p>
                </CardContent>
              </Card>
            </Link>
            <Link
              to={cityCanonical}
              title={`Tutti i compro oro verificati a ${zona.cityName}`}
              className="block"
            >
              <Card className="h-full hover:border-primary transition-colors">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-1">Compro Oro {zona.cityName}</h3>
                  <p className="text-xs text-muted-foreground">
                    Mappa e classifica completa dei compro oro OAM verificati in tutta {zona.cityName}.
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}
